import * as React from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import Service from '@/app/utils/service';
import API from '@/app/api/internalAPIs';
import {
  WithStyles,
  Button,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Card,
  CardContent,
  CardActions,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import translate from '@/app/utils/translate';
import approveDocumentFormStyle from './approveDocumentFormStyle';
import { throwError } from '@/app/redux/error';
import { getFormValues, isInvalid, isPristine, isSubmitting } from 'redux-form';
import { sendUnauthorizedResponse, APPROVAL_STATUS } from '@/app/redux/documentApproval';
import FormFields from '../../FormFields/FormFields';
import { INPUT_TYPE } from '@/app/utils/helper';
import { getForm, changeData } from '../../Form/Form';

const validate = (values: ApprovalForm) => {
  const errors: {[k: string]: any} = {};
  const requiredFields = ['fApprovalStatus', 'fInfo'];

  requiredFields.forEach(field => {
    if (!values[field] || values[field] === '<p>&nbsp;</p>') {
      errors[field] = translate.t('laThisRequired');
    }
  });

  if (values.fInfo && values.fInfo.length > 80) {
    errors.fInfo = `${translate.t('laErrorMaxTextLen1')} 80`;
  }

  return errors;
};

const FORM_NAME = 'approveDocumentForm';

interface MapStateToProps {
  currentValues: ApprovalForm;
  entityId: number;
  documentURL: string;
  documentName: string;
  empId: number;
  accessToken: string;
  invalid: Function;
  pristine: Function;
  submitting: Function;
}

interface MapDispatchToProps {
  throwError: Function;
  sendResponse: Function;
}

type PropsType = WithStyles<typeof approveDocumentFormStyle> & MapDispatchToProps & MapStateToProps;

const emptyForm = () => {
  return {
    fApprovalStatus: '',
    fInfo: '',
  };
};

type ApprovalForm = {
  fApprovalStatus: string;
  fInfo: string;
};

type StateType = {
  formData: ApprovalForm;
  value: string;
};

class ApproveDocumentForm extends React.Component<PropsType> {
  state: StateType = {
    formData: emptyForm(),
    value: '',
  };
  Form: any;

  constructor(props: PropsType) {
    super(props);
    this.Form = getForm(FORM_NAME, validate);
  }

  componentDidMount() {
    const { formData } = this.state;
    Object.keys(formData).forEach(field => {
      changeData({form: FORM_NAME, field, value: formData[field]});
    });
  }

  handleDownload = () => {
    const docAppData = JSON.parse(localStorage.getItem('documentApprovalData'));
    const { documentName: fileName, accessToken } = this.props;

    Service.get(
      API.approveDocument.docApprovalFile(docAppData.entityId),
      (data: any) => {
        const blob = new Blob([data]);

        if (navigator.msSaveBlob) {
          // IE 10+
          navigator.msSaveBlob(blob, fileName);
        } else {
          let link = document.createElement('a');
          // Browsers that support HTML5 download attribute
          if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            const linkOptions = {
              href: url,
              download: fileName,
              target: '_blank',
              type: 'hidden',
            };

            link = Object.assign(link, linkOptions);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        }
      },
      (error: any) => this.props.throwError(error),
      {
        Authorization: `AccessToken ${accessToken}`,
        responseType: 'arraybuffer'
      }
    );
  };

  handleSend = () => {
    const { currentValues: cv, entityId, accessToken } = this.props;
    if (cv.fApprovalStatus === APPROVAL_STATUS.APPROVED) {
      changeData({form: FORM_NAME, field: 'fInfo', value: ''});
    }

    const response = {
      entityId,
      fInfo: cv.fApprovalStatus === APPROVAL_STATUS.APPROVED ? '' : cv.fInfo,
      approved: cv.fApprovalStatus === APPROVAL_STATUS.APPROVED,
      accessToken,
    };

    this.props.sendResponse(response);
  }

  handleChange = (event: any) => {
    const { value } = event.target;
    this.setState({ value });
    changeData({form: FORM_NAME, field: 'fApprovalStatus', value});
  };

  render() {
    const { classes, documentName, invalid, pristine, submitting } = this.props;
    const { formData, value } = this.state;
    const { Form } = this;
    const disabledSend = invalid(FORM_NAME) || submitting(FORM_NAME) || !value || pristine(FORM_NAME);

    return (
      <Card>
        <CardContent>
          <Typography component="h1" variant="h6">
            {translate.t('text_approve_document')}
          </Typography>
          <Typography component="p" variant="body2">
            {translate.t('text_document_tb_approved')}
          </Typography>
          <div className={classes.downloadContainer}>
            <a
              onClick={this.handleDownload}
              target="_blank"
              className={classes.downloadLink}
            >
              {documentName}
            </a>
            <Button
              variant="text"
              color="primary"
              size="small"
              onClick={this.handleDownload}
            >
              {translate.t('label_open')}
            </Button>
          </div>
          <Form formData={formData}>
            <FormControl className={classes.formControl} fullWidth>
              <FormLabel className={classes.formControlLabel}>
                {translate.t('label_approval_action')}
              </FormLabel>
              <RadioGroup
                aria-label={translate.t('text_approve_document')}
                name="fApprovalStatus"
                className={classes.group}
                value={value}
                onChange={this.handleChange}
              >
                <FormControlLabel
                  value={APPROVAL_STATUS.APPROVED}
                  control={<Radio color="primary" className={classes.radio} />}
                  label={translate.t('laApprove')}
                />
                <FormControlLabel
                  value={APPROVAL_STATUS.REJECTED}
                  control={<Radio color="primary" className={classes.radio} />}
                  label={translate.t('label_reject_request_changes')}
                />
              </RadioGroup>
            </FormControl>
            {value === APPROVAL_STATUS.REJECTED ? (
              <div className={classes.input}>
                <FormFields
                  inputs={[{
                    type: INPUT_TYPE.TEXTAREA,
                    code: 'fInfo',
                    name: 'fInfo',
                    label: translate.t('label_changes_reason'),
                    required: true,
                    order: 2,
                  }]}
                />
              </div>
              ) : null
            }
          </Form>
        </CardContent>
        <CardActions className={classes.buttonContainer}>
          <Button
            variant="contained"
            color="primary"
            onClick={this.handleSend}
            disabled={disabledSend}
          >
            {translate.t('label_send_response')}
          </Button>
        </CardActions>
      </Card>
    );
  }
}

const mapStateToProps = (state: any) => {
  const { documentApproval: docApp } = state;

  return {
    currentValues: getFormValues(FORM_NAME)(state),
    entityId: docApp.get('entityId'),
    documentURL: docApp.get('documentURL'),
    documentName: docApp.get('documentName'),
    empId: docApp.get('empId'),
    accessToken: docApp.get('accessToken'),
    invalid: (formName: string) => isInvalid(formName)(state),
    pristine: (formName: string) => isPristine(formName)(state),
    submitting: (formName: string) => isSubmitting(formName)(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  throwError: (err: any) => dispatch(throwError(err)),
  sendResponse: (data: any) => dispatch<any>(sendUnauthorizedResponse(data)),
});

const enhance = compose<any, any>(
  connect(mapStateToProps, mapDispatchToProps),
  withStyles(approveDocumentFormStyle, {withTheme: true}),
);

export default enhance(ApproveDocumentForm);
